import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    addReconciliationVerificationNote,
    addManagerReconciliationNote,
    createReconciliation,
    updateReconciliation,
    upsertAcknowledgeAndVerified,
    verifyDailyReconciliation,
} from 'services';
import AcknowledgeAndVerifiedModel from 'models/audit/AcknowledgeAndVerifiedModel';
import StoreAuditSummaryRangeModel from 'models/auditDashboard/StoreAuditSummaryRangeModel';

interface ReconciliationPayload {
    reconciliation_date: string;
    amount: string;
    reason: string;
}

interface UpsertAcknowledgeAndVerifiedPayload {
    reconciliation_date: string;
    is_verified: boolean;
    is_ack: boolean;
    notes: string;
}

interface ManagerReconciliationNotePayload {
    reconciliation_date: string;
    note: string;
}

interface VerifyDailyReconciliationPayload {
    reconciliation_date: string;
    verification_note: string;
}

type VerificationNotePayload = VerifyDailyReconciliationPayload;

const latestNoteText = (notes: unknown): string | null => {
    if (!Array.isArray(notes) || notes.length === 0) {
        return null;
    }

    const latest = notes[notes.length - 1] as Record<string, unknown>;
    const note = typeof latest?.note === 'string' ? latest.note.trim() : '';
    return note || null;
};

const buildCompactVerifiedPayload = (reconciliation: any) => ({
    is_verified: reconciliation?.is_verified === true,
    audit_status: reconciliation?.audit_status ?? (reconciliation?.is_verified === true ? 'approved' : 'needs_resolution'),
    status: reconciliation?.audit_status ?? (reconciliation?.is_verified === true ? 'approved' : 'needs_resolution'),
    date_time: reconciliation?.verified_at ?? null,
    user_id: reconciliation?.verified_by_user_id ?? null,
    user_name: reconciliation?.verified_by_user_name ?? null,
    note: latestNoteText(reconciliation?.verification_note) ?? reconciliation?.notes ?? null,
    verified_at: reconciliation?.verified_at ?? null,
    verified_by_user_name: reconciliation?.verified_by_user_name ?? null,
    has_manager_note: Array.isArray(reconciliation?.manager_note) && reconciliation.manager_note.length > 0,
    can_add_manager_note: reconciliation?.can_add_manager_note === true,
    can_verify: reconciliation?.can_verify === true,
});

export const applyReconciliationMutationResult = (
    queryClient: ReturnType<typeof useQueryClient>,
    storeId: string,
    reconciliationDate: string,
    response: any
) => {
    const reconciliation = response?.data && !Array.isArray(response.data) ? response.data : null;
    if (!response?.success || !reconciliation) {
        return;
    }

    queryClient.setQueryData(
        ['acknowledge-verified-details', storeId, reconciliationDate],
        AcknowledgeAndVerifiedModel.fromServerData(reconciliation)
    );

    queryClient.setQueriesData(
        { queryKey: ['audit-dashboard', 'store-audit-summary-range', storeId] },
        (current: StoreAuditSummaryRangeModel | undefined) => {
            if (!current) {
                return current;
            }

            const verified = buildCompactVerifiedPayload(reconciliation);
            return StoreAuditSummaryRangeModel.fromServerData({
                storeId: current.storeId,
                storeName: current.storeName,
                storeTimezone: current.storeTimezone,
                startDate: current.startDate,
                endDate: current.endDate,
                dailySummaries: current.dailySummaries.map((day) => (
                    day.reportDate === reconciliationDate ? { ...day, verified } : day
                )),
                overallTotals: current.overallTotals,
            });
        }
    );
};

const invalidateReconciliationViews = (
    queryClient: ReturnType<typeof useQueryClient>,
    storeId: string,
    reconciliationDate: string
) => {
    queryClient.invalidateQueries({ queryKey: ['acknowledge-verified-details', storeId, reconciliationDate] });
    queryClient.invalidateQueries({ queryKey: ['store-reconciliations', storeId] });
    queryClient.invalidateQueries({ queryKey: ['daily-expanded-report', storeId, reconciliationDate] });
    queryClient.invalidateQueries({ queryKey: ['store-audit-summaries'] });
    queryClient.invalidateQueries({ queryKey: ['audit-dashboard', 'store-audit-summary-range'] });
};

// hooks for reconciliation mutations
export const useUpsertAcknowledgeAndVerified = (storeId: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: UpsertAcknowledgeAndVerifiedPayload) => upsertAcknowledgeAndVerified(storeId, data),
        onSuccess: (response, variables) => {
            applyReconciliationMutationResult(queryClient, storeId, variables.reconciliation_date, response);
            invalidateReconciliationViews(queryClient, storeId, variables.reconciliation_date);
        },
    });
};

export const useAddManagerReconciliationNote = (storeId: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: ManagerReconciliationNotePayload) => addManagerReconciliationNote(storeId, data),
        onSuccess: (response, variables) => {
            applyReconciliationMutationResult(queryClient, storeId, variables.reconciliation_date, response);
            invalidateReconciliationViews(queryClient, storeId, variables.reconciliation_date);
        },
    });
};

export const useVerifyDailyReconciliation = (storeId: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: VerifyDailyReconciliationPayload) => verifyDailyReconciliation(storeId, data),
        onSuccess: (response, variables) => {
            applyReconciliationMutationResult(queryClient, storeId, variables.reconciliation_date, response);
            invalidateReconciliationViews(queryClient, storeId, variables.reconciliation_date);
        },
    });
};

export const useAppendReconciliationVerificationNote = (storeId: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: VerificationNotePayload) => addReconciliationVerificationNote(storeId, data),
        onSuccess: (response, variables) => {
            applyReconciliationMutationResult(queryClient, storeId, variables.reconciliation_date, response);
            invalidateReconciliationViews(queryClient, storeId, variables.reconciliation_date);
        },
    });
};

export const useCreateReconciliation = (storeId: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: ReconciliationPayload) => createReconciliation(storeId, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['store-reconciliations', storeId] });
            queryClient.invalidateQueries({ queryKey: ['daily-expanded-report', storeId, variables.reconciliation_date] });
        },
    });
};

export const useUpdateReconciliation = (storeId: string, date: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: ReconciliationPayload }) => updateReconciliation(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['store-reconciliations', storeId] });
            queryClient.invalidateQueries({ queryKey: ['daily-expanded-report', storeId, date] });
        },
    });
};
